/**
 * Miniature pli + main avant pose — réutilise MiniCard / layout NSEW (timeline replay).
 */

import { useMemo } from 'react';
import MiniCard from '../../components/MiniCard';
import type { CardNumber } from '../../types/game';
import { handsAtStartOfTrick, DEAL_SEAT_LABELS } from '../../utils/dealHandsAtTrick';
import type { DiffereStatsDealDetail } from '../../utils/differeStatsReplay';
import '../../styles/replay.css';

type ScreenPosition = 'bottom' | 'top' | 'left' | 'right';

function getScreenPositions(myPosition: number): Map<number, ScreenPosition> {
  const layout: ScreenPosition[] = ['bottom', 'right', 'top', 'left'];
  const positions = new Map<number, ScreenPosition>();
  for (let i = 0; i < 4; i++) {
    positions.set(i, layout[(i - myPosition + 4) % 4]!);
  }
  return positions;
}

function thumbClass(pos: ScreenPosition): string {
  switch (pos) {
    case 'bottom': return 'thumb-south';
    case 'top': return 'thumb-north';
    case 'left': return 'thumb-west';
    case 'right': return 'thumb-east';
    default: return 'thumb-south';
  }
}

export interface TrickHoverPreviewTarget {
  trickIndex: number;
  /** Index dans l'ordre de jeu du pli (0 = entame). */
  posInTrick: number;
  seat: number;
  card: number;
}

export default function DiffereStatsTrickHoverPreview({
  detail,
  target,
  x,
  y,
}: {
  detail: DiffereStatsDealDetail;
  target: TrickHoverPreviewTarget;
  x: number;
  y: number;
}) {
  const tr = detail.tricks.find(t => t.trickIndex === target.trickIndex);
  const viewSeat = detail.humanSeat ?? 0;
  const screenPositions = useMemo(() => getScreenPositions(viewSeat), [viewSeat]);

  const priorPlays = useMemo(() => {
    if (!tr) return [];
    const out: Array<{ card: number; player: number }> = [];
    for (let i = 0; i < target.posInTrick && i < tr.cards.length; i++) {
      out.push({
        card: tr.cards[i]!,
        player: tr.players[i] ?? ((tr.leader + i) % 4),
      });
    }
    return out;
  }, [tr, target.posInTrick]);

  const hand = useMemo(() => {
    const hands = handsAtStartOfTrick(detail, target.trickIndex + 1);
    return hands?.[target.seat] ?? [];
  }, [detail, target.trickIndex, target.seat]);

  const seatLabel = DEAL_SEAT_LABELS[target.seat] ?? `Siège ${target.seat}`;

  return (
    <div
      className="differe-stats-trick-hover-preview"
      style={{ left: x + 12, top: y + 18 }}
      onClick={e => e.stopPropagation()}
    >
      <div className="differe-stats-trick-hover-preview-section">
        <div className="differe-stats-trick-hover-preview-label">
          Pli {target.trickIndex + 1} — avant {seatLabel}
        </div>
        <div className="replay-timeline-thumbnail differe-stats-trick-hover-preview-thumb">
          {priorPlays.length === 0 ? (
            <span className="differe-stats-trick-hover-preview-empty">Entame</span>
          ) : (
            priorPlays.map((play, i) => {
              const pos = screenPositions.get(play.player) ?? 'bottom';
              return (
                <MiniCard
                  key={`${play.card}-${i}`}
                  card={play.card as CardNumber}
                  className={thumbClass(pos)}
                />
              );
            })
          )}
        </div>
      </div>
      <div className="differe-stats-trick-hover-preview-section">
        <div className="differe-stats-trick-hover-preview-label">
          Main {seatLabel} ({hand.length})
        </div>
        <div className="differe-stats-trick-hover-preview-hand">
          {hand.map((c, i) => (
            <MiniCard
              key={`${c}-${i}`}
              card={c as CardNumber}
              className={c === target.card ? 'differe-stats-trick-hover-preview-about-to-play' : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
